import { auth } from '@/lib/firebase';
import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { getAtomsForTier } from './atoms';
import { toast } from 'sonner';

export const useCreateInstance = (tier: string) => {
  const atoms = getAtomsForTier(tier);
  const setInstance = useSetAtom(atoms.instanceAtom);
  const setLoading = useSetAtom(atoms.loadingAtom);
  const setError = useSetAtom(atoms.errorAtom);

  const createInstance = useCallback(
    async (buildVersion?: string): Promise<void> => {
      const user = auth.currentUser;

      // Set loading state
      setLoading((prev) => ({ ...prev, create: true }));

      // Clear error state
      setError(null);

      try {
        if (!user) return;

        const idToken = await user.getIdToken();
        const response = await fetch('/api', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ action: 'create', tier, buildVersion }),
        });

        if (!response.ok) {
          throw new Error(`Failed to create instance: ${response.status}`);
        }

        const { data } = await response.json();

        // Update instance data
        setInstance(data || null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        toast.error(errorMessage);
        setError(errorMessage);
      } finally {
        // Clear loading state
        setLoading((prev) => ({ ...prev, create: false }));
      }
    },
    [tier, setInstance, setLoading, setError],
  );

  return {
    createInstance,
  };
};
